import axios from 'axios'
import request from '@/utils/request'
import { baseUrl } from '@/config/index'
import { getToken } from '@/utils/auth'
import { handleAuthExpired, isAuthExpiredError } from '@/utils/authExpired'

const publicApi = axios.create({
  baseURL: `${baseUrl.replace(/\/$/, '')}/api`,
  timeout: 100000
})

const unwrapPublicApiResponse = (response) => response.data

const getPublicApiErrorMessage = (error) => {
  const data = error && error.response ? error.response.data : null
  if (data && data.message) {
    return data.message
  }

  const errors = data && data.errors
  if (errors && typeof errors === 'object') {
    const firstKey = Object.keys(errors)[0]
    const firstValue = firstKey ? errors[firstKey] : null
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      return firstValue[0]
    }
  }

  return (error && error.message) || '请求失败'
}

const normalizePublicApiError = (error) => {
  const authExpired = isAuthExpiredError(error)

  if (authExpired) {
    handleAuthExpired()
  }

  const normalizedError = new Error(authExpired ? '登录已过期，请重新登录' : getPublicApiErrorMessage(error))
  normalizedError.response = error && error.response
  normalizedError.data = error && error.response ? error.response.data : null
  normalizedError.isAuthExpired = authExpired
  throw normalizedError
}

const buildPublicApiHeaders = (headers = {}) => {
  const token = getToken()
  const requestHeaders = {
    Accept: 'application/json',
    ...headers
  }

  if (token) {
    requestHeaders.Authorization = `Bearer ${token}`
  }

  return requestHeaders
}

const requestAuthenticatedPublicApi = (config) => {
  return publicApi({
    ...config,
    headers: buildPublicApiHeaders(config && config.headers ? config.headers : {})
  })
    .then(unwrapPublicApiResponse)
    .catch(normalizePublicApiError)
}

const MAX_DIRECT_UPLOAD_SIZE = 30 * 1024 * 1024
const DIRECT_UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'doc', 'docx']

const getDirectUploadFileName = (file) => {
  const filename = file && typeof file.name === 'string' ? file.name.trim() : ''
  const extension = filename.includes('.') ? filename.split('.').pop().toLowerCase() : ''

  if (!filename) {
    throw new Error('请选择要上传的文件')
  }
  if (filename.length > 255) {
    throw new Error('文件名不能超过255个字符')
  }
  if (!DIRECT_UPLOAD_EXTENSIONS.includes(extension)) {
    throw new Error('仅支持 jpg、jpeg、png、gif、webp、pdf、doc、docx 格式文件')
  }
  if (typeof file.size === 'number' && file.size > MAX_DIRECT_UPLOAD_SIZE) {
    throw new Error('文件大小不能超过30M')
  }

  return filename
}

const getDirectUploadSignature = (response) => {
  const signature = response && response.data && typeof response.data === 'object'
    ? response.data
    : response
  const headers = signature && signature.headers
  const contentTypeKey = headers && Object.keys(headers).find(key => key.toLowerCase() === 'content-type')

  if (!signature || typeof signature.upload_url !== 'string' || typeof signature.url !== 'string') {
    throw new Error('上传签名无效，请重试')
  }
  if (!headers || typeof headers !== 'object' || !contentTypeKey || !headers[contentTypeKey]) {
    throw new Error('上传签名缺少 Content-Type，请重试')
  }

  return signature
}

const getDirectUploadError = (error) => {
  const responseData = error && error.response ? error.response.data : null
  const message = responseData && typeof responseData === 'object' && responseData.message
    ? responseData.message
    : (error && error.message)
  const normalizedError = new Error(message || '文件上传失败，请重试')
  normalizedError.response = error && error.response
  normalizedError.data = responseData
  throw normalizedError
}

export const getCaptcha = () => {
  return publicApi.get('/captcha')
    .then(unwrapPublicApiResponse)
    .catch(normalizePublicApiError)
}

export const sendAuthSmsCode = (data) => {
  return publicApi.post('/sms/code', data)
    .then(unwrapPublicApiResponse)
    .catch(normalizePublicApiError)
}

export const registerAccount = (data) => {
  return publicApi.post('/register', data)
    .then(unwrapPublicApiResponse)
    .catch(normalizePublicApiError)
}

export const loginAccount = (data) => {
  return publicApi.post('/login', data)
    .then(unwrapPublicApiResponse)
    .catch(normalizePublicApiError)
}

export const getHomepage = () => {
  return publicApi.get('/homepage')
    .then(unwrapPublicApiResponse)
    .catch(normalizePublicApiError)
}

export const getMemberCertification = () => {
  return requestAuthenticatedPublicApi({
    url: '/certification',
    method: 'get'
  })
}

export const getMemberProfile = () => {
  return requestAuthenticatedPublicApi({
    url: '/member/profile',
    method: 'get'
  })
}

export const getAssetEvaluationOptions = () => {
  return requestAuthenticatedPublicApi({
    url: '/options',
    method: 'get'
  })
}

export const uploadMemberFile = async (file, type = 'image', options = {}) => {
  void type
  const filename = getDirectUploadFileName(file)
  const signatureResponse = await requestAuthenticatedPublicApi({
    url: '/upload/direct',
    method: 'post',
    data: { filename }
  })
  const signature = getDirectUploadSignature(signatureResponse)

  try {
    await axios({
      url: signature.upload_url,
      method: 'put',
      data: file,
      headers: { ...signature.headers },
      timeout: 600000,
      withCredentials: false,
      transformRequest: [data => data],
      validateStatus: status => status === 200,
      onUploadProgress: typeof options.onProgress === 'function' ? options.onProgress : undefined
    })
  } catch (error) {
    getDirectUploadError(error)
  }

  return {
    data: {
      url: signature.url,
      key: signature.key,
      expires_in: signature.expires_in
    }
  }
}

export const uploadMemberImage = (file, options = {}) => uploadMemberFile(file, 'image', options)

export const verifyMemberIdCard = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/certification/verify-id-card',
    method: 'post',
    data
  })
    .then((response) => {
      const verifyResult = response && response.data ? response.data : response
      if (verifyResult && Object.prototype.hasOwnProperty.call(verifyResult, 'isok') && !verifyResult.isok) {
        throw new Error(verifyResult.message || verifyResult.msg || '身份证错误请仔细核对')
      }
      return response
    })
}

export const submitIndividualCertification = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/certification/individual',
    method: 'post',
    data
  })
}

export const submitMcnCertification = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/certification/mcn',
    method: 'post',
    data
  })
}

export const createMemberPlatformAccount = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/member/platform-accounts',
    method: 'post',
    data
  })
}

export const updateMemberPlatformAccount = (id, data) => {
  return requestAuthenticatedPublicApi({
    url: `/member/platform-accounts/${id}`,
    method: 'put',
    data
  })
}

export const deleteMemberPlatformAccount = (id) => {
  return requestAuthenticatedPublicApi({
    url: `/member/platform-accounts/${id}`,
    method: 'delete'
  })
}

export const updateMemberAvatar = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/member/avatar',
    method: 'put',
    data
  })
}

export const updateMemberName = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/member/name',
    method: 'put',
    data
  })
}

export const submitAssetEvaluation = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/asset-evaluations',
    method: 'post',
    data
  })
}

export const submitCooperationApplication = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/cooperation-applications',
    method: 'post',
    data
  })
}

export const logoutMemberSession = () => {
  return requestAuthenticatedPublicApi({
    url: '/logout',
    method: 'post'
  })
}

export const saveAssetEvaluationDraft = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/asset-evaluations/draft',
    method: 'post',
    data
  })
}

export const getAssetEvaluationDraft = () => {
  return requestAuthenticatedPublicApi({
    url: '/asset-evaluations/draft',
    method: 'get'
  })
}

export const getAssetEvaluationList = (params = {}) => {
  return requestAuthenticatedPublicApi({
    url: '/asset-evaluations',
    method: 'get',
    params
  })
}

export const getAssetEvaluationDetail = (id) => {
  return requestAuthenticatedPublicApi({
    url: `/asset-evaluations/${id}`,
    method: 'get'
  })
}

export const createStreamerProfile = (data) => {
  return requestAuthenticatedPublicApi({
    url: '/streamer-profiles',
    method: 'post',
    data
  })
}

export const batchCreateStreamerProfiles = (files = []) => {
  const formData = new FormData()
  files.forEach(file => {
    formData.append('files[]', file)
  })

  return requestAuthenticatedPublicApi({
    url: '/streamer-profiles/batch',
    method: 'post',
    data: formData
  })
}

export const getStreamerProfileList = (params = {}) => {
  return requestAuthenticatedPublicApi({
    url: '/streamer-profiles',
    method: 'get',
    params
  })
}

export const getStreamerProfileDetail = (id) => {
  return requestAuthenticatedPublicApi({
    url: `/streamer-profiles/${id}`,
    method: 'get'
  })
}

export const updateStreamerProfile = (id, data) => {
  return requestAuthenticatedPublicApi({
    url: `/streamer-profiles/${id}`,
    method: 'put',
    data
  })
}

export const deleteStreamerProfile = (id) => {
  return requestAuthenticatedPublicApi({
    url: `/streamer-profiles/${id}`,
    method: 'delete'
  })
}

// 管理员登录接口
export function login(param) {
  return request({
    url: '/v1/admins/login',
    method: 'post',
    data: param
  }).then(response => {
    console.log('API request successful:', response); // 打印API请求成功的响应
    return response;
  }).catch(error => {
    console.error('API request failed:', error); // 打印API请求失败的错误
    throw error;
  });
}

export const loginNum = (inStorageNo) => {
  return request({
    url: `/client/anno/loginNum?phone=${inStorageNo}`,
    method: 'get'
  })
}

// 管理员登出接口
export const logout = () => {
  return request({
    url: '/api/user/logout',
    method: 'post'
  })
}

// 获取管理员信息
export const getInfo = (token) => {
  return request({
    url: '/client/anno/getRoles',
    method: 'get',
    params: { token }
  })
}

// 发送手机登录验证码
export const sendMobileVerifyCode = (data) => {
  return request({
    url: '/client/anno/sendMobileVerifyCode',
    method: 'post',
    data: data
  })
}
// 对比验证码的正确情况
export const compareVerifycode = (data) => {
  return request({
    url: '/client/anno/compareVerifycode',
    method: 'post',
    data: data
  })
}
// 注册	/client/customer/register
export const register = (data) => {
  return request({
    url: '/client/customer/register',
    method: 'post',
    data: data
  })
}
// 修改密码
export const resetpass = (data) => {
  return request({
    url: '/client/customer/resetpass',
    method: 'post',
    data: data
  })
}
export const getSignTypeList = () => {
  return request({
    url: `/sys/dict/listBusiness`,
    method: 'GET'
  })
}

export const zcDelectFile = (param) => {
  console.log(param)
  return request({
    url: `/client/cloud/file/deleteTransport`,
    method: 'POST',
    data: param
  })
}
